import type { WeeklyMetricKey, Weekday } from './types';
import { CLASS_THEME_COLORS } from '../../services/classTheme';

/** Monday-based work week (matches Working Hours Calculator). */
export const WEEK_STARTS_ON = 1 as const;

export const WORK_WEEKDAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

export const JS_DAY_TO_WEEKDAY: Record<number, Weekday | null> = {
  0: null,
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: null,
};

/** Editable body rows (off-day + total live in each day column header strip). */
export const WEEKLY_METRICS: WeeklyMetricKey[] = ['shiftTime'];

export const WEEKLY_DISPLAY_ROWS: WeeklyMetricKey[] = WEEKLY_METRICS;

/** Paid portion of lunch always counted as work (Québec educators — 0.5h paid lunch). */
export const PAID_LUNCH_MINUTES = 30;

/** Standard unpaid lunch length beyond the paid half-hour (1h lunch total). */
export const DEFAULT_LUNCH_MINUTES = 60;

/**
 * Default paid shift length (8.5h). With a 1h lunch (0.5h unpaid), clock span is 9h —
 * e.g. 07:00 → 16:00, 08:00 → 17:00.
 */
export const DEFAULT_SHIFT_PAID_MINUTES = 8.5 * 60;

/** Clock span for a default shift: paid hours + unpaid lunch. */
export const DEFAULT_SHIFT_CLOCK_MINUTES =
  DEFAULT_SHIFT_PAID_MINUTES + (DEFAULT_LUNCH_MINUTES - PAID_LUNCH_MINUTES);

export const DEFAULT_DAY_SHIFT = {
  start: '08:00',
  end: '17:00',
  lunchStart: '12:00',
  lunchEnd: '13:00',
  roomIds: [] as string[],
  isOff: false,
};

/** Opening duty default start time → end is start + 9h clock (07:00–16:00). */
export const OPENING_DEFAULT_START = '07:00';

/** Closing duty default end time → start is end − 9h clock (09:00–18:00). */
export const CLOSING_DEFAULT_END = '18:00';

/** Closing duty default start (pairs with {@link CLOSING_DEFAULT_END} for 8.5h paid). */
export const CLOSING_DEFAULT_START = '09:00';

export const CLASS_LABEL_COLORS = CLASS_THEME_COLORS;

export const SHIFT_TIME_INPUT_CLASS =
  'h-8 w-[5rem] min-w-[4.5rem] max-w-[5.25rem] shrink-0 px-1.5 text-sm tabular-nums';

/** Compact pill times — clock icon hidden; sized for readability in day cells. */
export const SHIFT_TIME_BUBBLE_CLASS =
  'h-7 min-w-0 rounded-md border-gray-200 bg-white px-1 text-center text-xs font-medium leading-none tabular-nums shadow-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none';

export const SHIFT_SELECT_CLASS =
  'h-8 w-full min-w-[7rem] rounded-lg border border-gray-300 bg-white px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

export const SHIFT_STORAGE_KEY = 'garderie-shift-weekly-schedules';

export const GRID_LAYOUT = {
  teacherColumnMinPx: 168,
  dayColumnMinPx: 228,
  /**
   * Sticky column z-index stack — must stay below manager portal chrome (header z-20).
   * Shared grid header > employee body columns > per-employee header row (employee card).
   */
  zSharedTeacher: 15,
  zTeacher: 12,
  zEmployeeHeaderTeacher: 8,
} as const;

export const STAFF_AVATAR_PALETTE = [
  'bg-secondary-100 text-secondary-800 border-secondary-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-violet-100 text-violet-800 border-violet-200',
  'bg-rose-100 text-rose-800 border-rose-200',
] as const;

export const STAFF_BLOCK_PALETTE = [
  'bg-secondary-200/90 border-secondary-300 text-secondary-900',
  'bg-emerald-200/90 border-emerald-300 text-emerald-900',
  'bg-sky-200/90 border-sky-300 text-sky-900',
  'bg-amber-200/90 border-amber-300 text-amber-900',
  'bg-violet-200/90 border-violet-300 text-violet-900',
  'bg-rose-200/90 border-rose-300 text-rose-900',
] as const;

export const FALLBACK_BLOCK_COLOR = 'bg-slate-200/90 border-slate-300 text-slate-900';

export const SHIFT_LABELS_STORAGE_KEY = 'garderie-shift-labels';

/** @deprecated Prefer Class Management rooms — non-class Kitchen/Office/R/G are no longer used in shift UI. */
export const NON_CLASS_LABEL_PRESETS = [
  { preset: 'kitchen' as const, id: 'Kitchen', name: 'Kitchen', letter: 'K' },
  { preset: 'office' as const, id: 'Office', name: 'Office', letter: 'O' },
  { preset: 'replacement' as const, id: 'R', name: 'R', letter: 'R' },
  { preset: 'special-needs' as const, id: 'G', name: 'G', letter: 'G' },
] as const;

/** @deprecated Seed demo rooms removed — use real class / groupe labels only. */
export const SEED_ROOMS: readonly {
  id: string;
  name: string;
  subtitle?: string;
  letter: string;
  isFloating?: boolean;
}[] = [];

export const SEED_TEACHERS = [
  { id: 'seed-1', firstName: 'Alice', lastName: 'Martin' },
  { id: 'seed-2', firstName: 'Bob', lastName: 'Dupont' },
  { id: 'seed-3', firstName: 'Marie', lastName: 'Lavoie' },
] as const;
